import { Icon } from '@socialchorus/shared-ui-components';
import { useHistory } from 'react-router';

import { UserSuggestion } from '../../common/use-user-suggestions';
import DEFAULT_AVATAR_NEW from '../../models/advocate/avatar';
import { Link } from '../ui';

import './mention-menu.scss';

export interface MentionMenuItemData {
  id: string;
  name: string;
  avatar: UserSuggestion['avatar'];
  displayAttributes: string[];
}

interface MentionMenuItemProps {
  data: MentionMenuItemData;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
}

/**
 * A single user suggestion row, shared by the Lexical body editor's mention
 * plugin and the reference text input so both render identically. Markup mirrors
 * the original `MentionMenuItem` in `mention-plugin.tsx`.
 */
export function MentionMenuItem({
  data,
  isSelected,
  onSelect,
  onHover,
}: MentionMenuItemProps) {
  const history = useHistory();
  const profileUrl = history.createHref({ pathname: `/profiles/${data.id}` });

  return (
    <div
      className={`lexical-mention-entry${
        isSelected ? ' lexical-mention-entry--active' : ''
      }`}
      role="option"
      aria-selected={isSelected ? true : undefined}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect();
      }}
      onMouseEnter={onHover}
      ref={(el) => {
        if (isSelected && el) {
          el.scrollIntoView({ block: 'nearest' });
        }
      }}
    >
      <div className="lexical-mention-avatar">
        <img
          src={data.avatar.url || DEFAULT_AVATAR_NEW}
          alt={data.name}
          width={40}
          height={40}
        />
      </div>
      <div className="lexical-mention-entry-text">
        <div className="lexical-mention-name">{data.name}</div>
        {data.displayAttributes.length > 0 && (
          <div className="lexical-mention-details">
            {data.displayAttributes.join(' • ')}
          </div>
        )}
      </div>
      <Link
        href={profileUrl}
        target="_blank"
        className="lexical-mention-profile-link"
      >
        <Icon size={24}>open_in_new</Icon>
      </Link>
    </div>
  );
}
